import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Clock, LayoutGrid, X, Camera } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useProctor } from "./useProctor";
import { Instructions, Legend } from "./Instructions";

const LIMITS = { no_face: 5000, multiple_people: 2000, looking_away: 4000 };
const answered = (a) => !!(a && ((a.sel && a.sel.length) || (a.text && a.text.trim())));
function statusOf(a) {
  if (!a?.v) return 0;
  if (a.mk) return answered(a) ? 4 : 3;
  return answered(a) ? 2 : 1;
}
const PAL = ["bg-white border border-border text-navy", "bg-crimson text-white", "bg-emerald-600 text-white", "bg-violet-600 text-white", "bg-violet-600 text-white ring-2 ring-emerald-400"];

function Palette({ questions, answers, idx, go, user, counts, onSubmit, close }) {
  return (
    <div className="h-full flex flex-col bg-white" data-testid="ts-palette">
      <div className="flex items-center justify-between p-4 border-b border-border"><span className="font-semibold text-navy text-sm truncate">{user?.name}</span>{close && <button onClick={close} data-testid="ts-palette-close"><X size={18} /></button>}</div>
      <div className="p-4 border-b border-border"><Legend counts={counts} /></div>
      <div className="p-4 flex-1 overflow-y-auto"><div className="grid grid-cols-5 gap-2">
        {questions.map((q, i) => <button key={q.id} onClick={() => go(i)} className={`h-9 rounded-md text-sm font-bold transition-transform hover:scale-105 ${PAL[statusOf(answers[q.id])]} ${i === idx ? "outline outline-2 outline-offset-2 outline-navy" : ""}`} data-testid={`ts-pal-${i + 1}`}>{i + 1}</button>)}
      </div></div>
      <div className="p-4 border-t border-border"><Button className="w-full viriks-navy text-white" onClick={onSubmit} data-testid="ts-submit-open">Submit Test</Button></div>
    </div>
  );
}

function QuestionView({ q, n, a, setA, neg }) {
  const pick = (i) => setA((cur) => ({ ...cur, sel: q.multi ? ((cur.sel || []).includes(i) ? cur.sel.filter((x) => x !== i) : [...(cur.sel || []), i]) : [i] }));
  return (
    <div className="p-5 md:p-8" data-testid="ts-question">
      <div className="flex items-center justify-between border-b border-border pb-3 text-sm">
        <span className="font-bold text-navy">Question No. {n}</span>
        <span className="flex gap-3 text-xs"><span className="text-emerald-700 font-semibold">+{q.marks}</span>{q.type !== "subjective" && neg > 0 && <span className="text-crimson font-semibold">-{neg}</span>}<span className="text-muted-foreground">{q.type === "subjective" ? "Written" : q.multi ? "Multiple correct" : "Single correct"}</span></span>
      </div>
      <p className="mt-5 text-navy text-base leading-relaxed whitespace-pre-line select-none" data-testid="ts-question-text">{q.question}</p>
      {q.type === "subjective" ? (
        <div className="mt-5"><Textarea rows={10} value={a.text || ""} onChange={(e) => setA((cur) => ({ ...cur, text: e.target.value }))} onPaste={(e) => e.preventDefault()} placeholder="Type your answer here" data-testid="ts-written-answer" />
          <p className="text-xs text-muted-foreground mt-1">{(a.text || "").trim().split(/\s+/).filter(Boolean).length} words · pasting is disabled</p></div>
      ) : (
        <div className="mt-5 space-y-3">{q.options.map((o, i) => {
          const on = (a.sel || []).includes(i);
          return <button key={i} onClick={() => pick(i)} className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-lg border transition-colors ${on ? "border-crimson bg-crimson/5" : "border-border hover:border-navy/30"}`} data-testid={`ts-option-${i}`}>
            <span className={`mt-0.5 h-5 w-5 shrink-0 border-2 flex items-center justify-center text-[10px] font-bold ${q.multi ? "rounded" : "rounded-full"} ${on ? "bg-crimson border-crimson text-white" : "border-gray-300 text-gray-400"}`}>{String.fromCharCode(65 + i)}</span><span className="select-none">{o}</span></button>;
        })}</div>
      )}
    </div>
  );
}

function useMonitor(active, proctor, violate, snap) {
  useEffect(() => {
    if (!active) return undefined;
    const since = { no_face: 0, multiple_people: 0, looking_away: 0 };
    const startAt = Date.now();
    const tick = setInterval(() => {
      if (proctor.cameraLost()) return violate("camera_off");
      const r = proctor.detect();
      if (!r || Date.now() - startAt < 4000) return;
      const now = Date.now();
      const flags = { no_face: r.faces === 0, multiple_people: r.faces > 1, looking_away: r.faces === 1 && r.away };
      for (const k of Object.keys(flags)) {
        if (!flags[k]) { since[k] = 0; continue; }
        since[k] = since[k] || now;
        if (now - since[k] >= LIMITS[k]) return violate(k);
      }
    }, 600);
    const periodic = setInterval(() => snap("periodic"), 30000);
    const onHide = () => { if (document.hidden) violate("tab_switch"); };
    const onBlur = () => setTimeout(() => { if (!document.hasFocus()) violate("tab_switch"); }, 800);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    document.addEventListener("copy", block);
    return () => { clearInterval(tick); clearInterval(periodic); document.removeEventListener("visibilitychange", onHide); window.removeEventListener("blur", onBlur); document.removeEventListener("contextmenu", block); document.removeEventListener("copy", block); };
  }, [active, proctor, violate, snap]);
}

export default function ExamAttempt() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const proctor = useProctor();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("instructions");
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(0);
  const [palOpen, setPalOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const done = useRef(false);
  const ansRef = useRef({});
  ansRef.current = answers;

  useEffect(() => {
    api.post(`/testseries/tests/${testId}/start`).then(({ data: d }) => {
      d.offset = Date.now() - new Date(d.server_now).getTime();
      setData(d); setAnswers(d.attempt.answers || {});
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate(-1); });
  }, [testId, navigate]);

  const aid = data?.attempt.id;
  const finish = useCallback((reason) => { done.current = true; document.exitFullscreen?.().catch(() => {}); navigate(`/learner/test-series/result/${aid}`, { replace: true, state: { reason } }); }, [aid, navigate]);
  const snap = useCallback((kind) => { const image = proctor.capture(); if (image) api.post(`/testseries/attempts/${aid}/snapshot`, { kind, image }).catch(() => {}); }, [aid, proctor]);
  const violate = useCallback(async (type) => {
    if (done.current) return;
    done.current = true;
    toast.error("Proctoring violation detected — your test has been submitted");
    try { await api.post(`/testseries/attempts/${aid}/snapshot`, { kind: "violation", violation: type, image: proctor.capture(), answers: ansRef.current }); } catch { /* server keeps saved answers */ }
    finish("violation");
  }, [aid, proctor, finish]);
  const submit = useCallback(async (reason = "manual") => {
    if (done.current) return;
    done.current = true;
    try { await api.post(`/testseries/attempts/${aid}/submit`, { answers: ansRef.current, reason }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finish(reason);
  }, [aid, finish]);

  useMonitor(stage === "test", proctor, violate, snap);
  useEffect(() => {
    if (stage !== "test") return undefined;
    const end = new Date(data.attempt.ends_at).getTime() + data.offset;
    const t = setInterval(() => { const s = Math.max(0, Math.round((end - Date.now()) / 1000)); setLeft(s); if (s === 0) submit("time_up"); }, 1000);
    const save = setInterval(() => { if (!done.current) api.post(`/testseries/attempts/${aid}/save`, { answers: ansRef.current }).catch(() => {}); }, 15000);
    return () => { clearInterval(t); clearInterval(save); };
  }, [stage, data, aid, submit]);

  const qs = useMemo(() => data?.questions || [], [data]);
  const q = qs[idx];
  const setA = (fn) => setAnswers((all) => ({ ...all, [q.id]: { v: true, ...fn(all[q.id] || {}) } }));
  const go = (i) => { setIdx(i); setAnswers((all) => ({ ...all, [qs[i].id]: { ...(all[qs[i].id] || {}), v: true } })); setPalOpen(false); };
  const next = (mark) => { setAnswers((all) => ({ ...all, [q.id]: { ...(all[q.id] || {}), v: true, mk: mark } })); api.post(`/testseries/attempts/${aid}/save`, { answers: ansRef.current }).catch(() => {}); if (idx < qs.length - 1) go(idx + 1); };
  const counts = useMemo(() => qs.reduce((c, x) => { c[statusOf(answers[x.id])] += 1; return c; }, [0, 0, 0, 0, 0]), [qs, answers]);
  const sections = useMemo(() => [...new Set(qs.map((x) => x.section))], [qs]);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 border-4 border-crimson border-t-transparent rounded-full animate-spin" /></div>;
  if (stage === "instructions") return <Instructions test={data.test} proctor={proctor} onStart={() => { document.documentElement.requestFullscreen?.().catch(() => {}); setStage("test"); go(0); setTimeout(() => snap("start"), 1500); }} />;

  const mm = String(Math.floor(left / 60)).padStart(2, "0"), ss = String(left % 60).padStart(2, "0");
  const pal = (close) => <Palette questions={qs} answers={answers} idx={idx} go={go} user={user} counts={counts} onSubmit={() => setConfirm(true)} close={close} />;
  return (
    <div className="h-screen flex flex-col bg-white select-none" data-testid="ts-exam">
      <header className="bg-navy text-white px-4 py-2 flex items-center gap-4">
        <span className="font-heading font-bold truncate flex-1 text-sm md:text-base">{data.test.title}</span>
        <span className={`flex items-center gap-2 font-mono font-bold text-sm px-3 py-1 rounded ${left < 300 ? "bg-crimson" : "bg-white/10"}`} data-testid="ts-timer"><Clock size={15} />{mm}:{ss}</span>
        <div className="relative h-12 w-16 rounded overflow-hidden bg-black border border-white/20" data-testid="ts-cam-mini"><video ref={proctor.videoRef} muted playsInline className="h-full w-full object-cover -scale-x-100" /><span className="absolute top-0.5 left-0.5 h-2 w-2 rounded-full bg-crimson animate-pulse" /></div>
        <button className="lg:hidden" onClick={() => setPalOpen(true)} data-testid="ts-palette-open"><LayoutGrid size={20} /></button>
      </header>
      <div className="flex gap-1 px-4 py-2 border-b border-border overflow-x-auto text-xs">
        {sections.map((s) => <button key={s} onClick={() => go(qs.findIndex((x) => x.section === s))} className={`px-3 py-1.5 rounded-md whitespace-nowrap ${q.section === s ? "bg-navy text-white" : "bg-muted"}`} data-testid={`ts-section-${s}`}>{s}</button>)}
        <span className="ml-auto flex items-center gap-1 text-muted-foreground whitespace-nowrap"><Camera size={12} className="text-crimson" />Proctored</span>
      </div>
      <div className="flex-1 flex min-h-0">
        <main className="flex-1 overflow-y-auto"><QuestionView q={q} n={idx + 1} a={answers[q.id] || {}} setA={setA} neg={data.test.negative_marking} /></main>
        <aside className="hidden lg:block w-80 border-l border-border">{pal()}</aside>
      </div>
      <footer className="border-t border-border px-4 py-3 flex flex-wrap gap-2 bg-muted/40">
        <Button variant="outline" size="sm" onClick={() => next(true)} data-testid="ts-mark-next">Mark for Review & Next</Button>
        <Button variant="outline" size="sm" onClick={() => setA(() => ({ sel: [], text: "" }))} data-testid="ts-clear">Clear Response</Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => go(idx - 1)} data-testid="ts-prev">Previous</Button>
        <Button size="sm" className="viriks-amber text-white" onClick={() => next(false)} data-testid="ts-save-next">Save & Next</Button>
      </footer>
      {palOpen && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setPalOpen(false)}><div className="absolute right-0 top-0 h-full w-80 max-w-[85vw]" onClick={(e) => e.stopPropagation()}>{pal(() => setPalOpen(false))}</div></div>}
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="ts-submit-dialog">
          <DialogHeader><DialogTitle>Submit test?</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">{[["Answered", counts[2] + counts[4]], ["Not answered", counts[1] + counts[3]], ["Marked for review", counts[3] + counts[4]], ["Not visited", counts[0]]].map(([l, v]) => <div key={l} className="bg-muted rounded-lg p-3"><div className="font-heading text-2xl font-black text-navy">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>)}</div>
          <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={() => setConfirm(false)} data-testid="ts-submit-cancel">Back to test</Button><Button className="viriks-amber text-white" onClick={() => submit("manual")} data-testid="ts-submit-confirm">Yes, submit</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
